import axios from 'axios'
import { API_BASE_URL } from 'config/api'
import { Cookies } from 'react-cookie'

// Types
export interface AuthToken {
  accessToken: string
  refreshToken: string
  expiresAt: number
}

export interface RefreshTokenResponse {
  access_token: string
  refresh_token: string
  expires_in: number
}

// Custom error class for authentication errors
export class AuthError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'AuthError'
  }
}

// Subscriber pattern for React state synchronization
type TokenSubscriber = (token: AuthToken | null) => void
const subscribers = new Set<TokenSubscriber>()

// Mutex lock to prevent concurrent refresh requests
let refreshMutex: Promise<string> | null = null

// Global auth error handler
let authErrorHandler: (() => void) | null = null

/**
 * Subscribe to token changes
 * Returns an unsubscribe function
 */
export function subscribeToTokenChanges(callback: TokenSubscriber): () => void {
  subscribers.add(callback)
  return () => subscribers.delete(callback)
}

/**
 * Notify all subscribers of token changes
 */
function notifySubscribers(token: AuthToken | null): void {
  subscribers.forEach((callback) => callback(token))
}

/**
 * Set global auth error handler
 * Called when token refresh fails or user is unauthorized
 */
export function setAuthErrorHandler(handler: () => void): void {
  authErrorHandler = handler
}

/**
 * Trigger global auth error handler
 */
function triggerAuthError(): void {
  if (authErrorHandler) {
    authErrorHandler()
  }
}

/**
 * Get auth token from cookie
 */
export function getAuthTokenFromCookie(): AuthToken | null {
  const cookies = new Cookies()
  const cookieData = cookies.get('@bookstory-auth-token')

  if (!cookieData?.user?.authToken) {
    return null
  }

  return cookieData.user.authToken
}

/**
 * Update auth token in cookie
 */
export function updateAuthCookie(authToken: AuthToken): void {
  const cookies = new Cookies()

  const diskState = { user: { authToken } }

  // Cookie should live longer than access token (30 days)
  const longTermExpiry = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
  const cookieOptions = {
    expires: longTermExpiry,
    path: '/',
    sameSite: 'lax' as const,
    secure: process.env.NODE_ENV === 'production',
  }

  cookies.set('@bookstory-auth-token', diskState, cookieOptions)

  // Notify subscribers of token change
  notifySubscribers(authToken)
}

/**
 * Clear auth token from cookie
 */
export function clearAuthCookie(): void {
  const cookies = new Cookies()
  cookies.remove('@bookstory-auth-token', { path: '/' })

  // Notify subscribers that token is cleared
  notifySubscribers(null)
}

/**
 * Check if token is expired or will expire soon
 */
export function isTokenExpiringSoon(
  expiresAt: number | undefined,
  bufferMinutes: number = 5
): boolean {
  if (!expiresAt) return true
  const BUFFER_TIME = bufferMinutes * 60 * 1000
  return Date.now() + BUFFER_TIME >= expiresAt
}

/**
 * Perform token refresh with the API
 * This is the low-level refresh function
 */
async function performTokenRefresh(refreshToken: string): Promise<RefreshTokenResponse> {
  try {
    const response = await axios({
      method: 'POST',
      url: `${API_BASE_URL}/oauth/token`,
      data: {
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
      },
      headers: {
        'Content-Type': 'application/json',
      },
    })

    return response.data
  } catch (error) {
    console.error('Token refresh API call failed:', error)
    throw new AuthError('Token refresh failed')
  }
}

/**
 * Refresh tokens and update cookie
 * Protected by mutex to prevent race conditions
 * Returns the new access token
 */
async function refreshAndUpdateTokens(refreshToken: string): Promise<string> {
  try {
    const result = await performTokenRefresh(refreshToken)

    const newAuthToken: AuthToken = {
      accessToken: result.access_token,
      refreshToken: result.refresh_token,
      expiresAt: Date.now() + result.expires_in * 1000,
    }

    // Update cookie and notify subscribers
    updateAuthCookie(newAuthToken)

    return newAuthToken.accessToken
  } catch (error) {
    // Clear tokens on refresh failure
    clearAuthCookie()
    triggerAuthError()
    throw error
  }
}

/**
 * Get a valid access token
 * Automatically refreshes if token is expired or expiring soon
 * Uses mutex to prevent concurrent refresh requests
 * 
 * @throws {AuthError} If no token exists or refresh fails
 */
export async function getValidAccessToken(): Promise<string> {
  const authToken = getAuthTokenFromCookie()

  if (!authToken) {
    throw new AuthError('No authentication token')
  }

  // If token is still valid, return it
  if (!isTokenExpiringSoon(authToken.expiresAt)) {
    return authToken.accessToken
  }

  // If refresh is already in progress, wait for it
  if (refreshMutex) {
    return refreshMutex
  }

  // Start refresh with mutex lock
  refreshMutex = refreshAndUpdateTokens(authToken.refreshToken).finally(() => {
    refreshMutex = null
  })

  return refreshMutex
}

/**
 * Check if an error is an authentication error
 */
export function isAuthError(error: any): boolean {
  return error instanceof AuthError
}

/**
 * Check if an HTTP error response is an auth error (401/403)
 */
export function isHttpAuthError(error: any): boolean {
  if (typeof error !== 'object' || !error) return false
  
  const status = error.response?.status
  const code = error.response?.data?.code
  
  return (
    status === 401 ||
    status === 403 ||
    (status === 403 && code === 'EXPIRED_ACCESS_TOKEN')
  )
}

